import React, { useState } from 'react';
import { 
  Globe, 
  Search, 
  Target, 
  TrendingUp, 
  Zap,
  CheckCircle,
  Clock,
  Users,
  DollarSign,
  BarChart3,
  Lightbulb,
  MapPin,
  Calendar,
  ArrowRight,
  ExternalLink,
  Loader2,
  Sparkles,
  Play,
  Download,
  Eye,
  Settings,
  ChevronRight,
  ChevronDown,
  Package,
  ShoppingCart,
  Star,
  AlertCircle
} from 'lucide-react';
import axios from 'axios';

export default function MomentumBuilder() {
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [roadmapData, setRoadmapData] = useState(null);
  const [activeMonth, setActiveMonth] = useState(1);
  const [expandedChannels, setExpandedChannels] = useState(new Set(['google', 'meta']));
  const [error, setError] = useState(null);

  const analyzeWebsite = async () => {
    if (!websiteUrl) return;
    
    setIsAnalyzing(true);
    setError(null);
    
    try {
      console.log('🚀 Starting website analysis...');
      
      const response = await axios.post('/api/momentum/analyze-website', {
        websiteUrl: websiteUrl
      });
      
      if (response.data.success) {
        setRoadmapData(response.data.roadmap);
        setAnalysisComplete(true);
        console.log('✅ Analysis completed successfully');
      } else {
        throw new Error(response.data.error || 'Analysis failed');
      }
      
    } catch (error) {
      console.error('❌ Analysis error:', error);
      setError(error.response?.data?.error || error.message || 'Analysis failed');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const toggleChannelExpansion = (channel) => {
    const newExpanded = new Set(expandedChannels);
    if (newExpanded.has(channel)) {
      newExpanded.delete(channel);
    } else {
      newExpanded.add(channel);
    }
    setExpandedChannels(newExpanded);
  };

  const getCampaignTypeColor = (type) => {
    const colors = {
      'Search': 'bg-blue-100 text-blue-800',
      'Shopping': 'bg-green-100 text-green-800',
      'Performance Max': 'bg-purple-100 text-purple-800',
      'Display': 'bg-orange-100 text-orange-800',
      'YouTube': 'bg-red-100 text-red-800',
      'Discovery': 'bg-pink-100 text-pink-800',
      'Traffic': 'bg-cyan-100 text-cyan-800',
      'Conversion': 'bg-emerald-100 text-emerald-800',
      'Retargeting': 'bg-amber-100 text-amber-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'High': 'text-red-600 bg-red-50',
      'Medium': 'text-yellow-600 bg-yellow-50',
      'Low': 'text-green-600 bg-green-50'
    };
    return colors[priority] || 'text-gray-600 bg-gray-50';
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-gradient-to-r from-green-500 to-blue-600 rounded-lg">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-xl font-semibold text-gray-800">Momentum Builder</h1>
            </div>
            <div className="text-sm text-gray-500">
              AI-Powered Marketing Roadmap Generator
            </div>
          </div>
          
          {analysisComplete && (
            <div className="flex items-center gap-3">
              <button className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors">
                <Eye className="w-4 h-4" />
                Preview
              </button>
              <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                <Download className="w-4 h-4" />
                Export Roadmap
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 bg-gray-50 min-h-screen">
        {!analysisComplete ? (
          /* Analysis Input Section */
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-3">
                Transform Your Digital Presence
              </h2>
              <p className="text-gray-600">
                Enter your website URL and let our AI analyze your sitemap, categories, and business structure to create a comprehensive marketing roadmap
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Website URL
                </label>
                <div className="flex gap-3">
                  <div className="flex-1 relative">
                    <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="url"
                      value={websiteUrl}
                      onChange={(e) => setWebsiteUrl(e.target.value)}
                      placeholder="https://yourwebsite.com"
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={isAnalyzing}
                    />
                  </div>
                  <button
                    onClick={analyzeWebsite}
                    disabled={isAnalyzing || !websiteUrl}
                    className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
                      isAnalyzing || !websiteUrl
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-gradient-to-r from-green-500 to-blue-600 text-white hover:from-green-600 hover:to-blue-700'
                    }`}
                  >
                    {isAnalyzing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        Analyze Website
                      </>
                    )}
                  </button>
                </div>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 text-red-800">
                    <AlertCircle className="w-5 h-5" />
                    <span className="font-medium">Analysis Failed</span>
                  </div>
                  <p className="text-red-700 mt-1 text-sm">{error}</p>
                </div>
              )}

              {isAnalyzing && (
                <div className="space-y-4">
                  <div className="text-center text-gray-600 mb-6">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-blue-600" />
                    <h3 className="font-medium mb-2">Analyzing Your Website...</h3>
                    <p className="text-sm">This may take a few moments</p>
                  </div>

                  <div className="space-y-3">
                    {[
                      'Scanning sitemap.xml file...',
                      'Extracting website categories...',
                      'Analyzing business structure...',
                      'AI-powered business analysis...',
                      'Generating campaign recommendations...',
                      'Creating monthly roadmap...'
                    ].map((step, index) => (
                      <div key={index} className="flex items-center gap-3 text-sm text-gray-600">
                        <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                        {step}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-800 mb-2">What we'll analyze:</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Sitemap.xml and website structure</li>
                  <li>• Product/service categories</li>
                  <li>• Business type and industry</li>
                  <li>• Target audience identification</li>
                  <li>• Competitive positioning</li>
                  <li>• Channel-specific campaign strategies</li>
                </ul>
              </div>
            </div>
          </div>
        ) : (
          /* Roadmap Results Section */
          <div className="space-y-6">
            {/* Website Analysis Overview */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-blue-600" />
                Website Analysis
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h3 className="font-medium text-blue-800">Business Type</h3>
                  <p className="text-blue-700">{roadmapData.businessAnalysis.businessType}</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <h3 className="font-medium text-green-800">Industry</h3>
                  <p className="text-green-700">{roadmapData.businessAnalysis.industry}</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg">
                  <h3 className="font-medium text-purple-800">Total Pages</h3>
                  <p className="text-purple-700">{roadmapData.websiteAnalysis.totalPages}</p>
                </div>
                <div className="p-4 bg-orange-50 rounded-lg">
                  <h3 className="font-medium text-orange-800">Categories Found</h3>
                  <p className="text-orange-700">{Object.keys(roadmapData.websiteAnalysis.categories).length}</p>
                </div>
              </div>

              {/* Categories */}
              <div>
                <h3 className="font-medium text-gray-800 mb-3">Discovered Categories</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(roadmapData.websiteAnalysis.categories).map(([categoryKey, items]) => (
                    <div key={categoryKey} className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-medium text-gray-800 capitalize mb-2">{categoryKey}</h4>
                      <div className="space-y-1">
                        {items.slice(0, 3).map((item, index) => (
                          <div key={index} className="text-sm text-gray-600 flex items-center gap-1">
                            <Package className="w-3 h-3" />
                            {item.name}
                          </div>
                        ))}
                        {items.length > 3 && (
                          <div className="text-xs text-gray-500">+{items.length - 3} more</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Monthly Timeline */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                4-Month Implementation Roadmap
              </h2>
              
              {/* Month Navigation */}
              <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg">
                {roadmapData.campaignStrategy.timeline.map((month) => (
                  <button
                    key={month.month}
                    onClick={() => setActiveMonth(month.month)}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                      activeMonth === month.month
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    Month {month.month}: {month.name}
                  </button>
                ))}
              </div>

              {/* Active Month Content */}
              {roadmapData.campaignStrategy.timeline.find(m => m.month === activeMonth) && (() => {
                const currentMonth = roadmapData.campaignStrategy.timeline.find(m => m.month === activeMonth);
                return (
                  <div className="space-y-6">
                    <div className="border-l-4 border-blue-500 pl-4">
                      <h3 className="text-lg font-semibold text-gray-800">
                        {currentMonth.name}
                      </h3>
                      <p className="text-gray-600 mt-1">
                        Focus: {currentMonth.focus}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-sm">
                        <span className="flex items-center gap-1 text-blue-600">
                          <DollarSign className="w-4 h-4" />
                          {currentMonth.budget}
                        </span>
                        <span className="flex items-center gap-1 text-green-600">
                          <CheckCircle className="w-4 h-4" />
                          Ready to implement
                        </span>
                      </div>
                    </div>

                    {/* Month-specific tasks and campaigns would go here */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <h4 className="font-medium text-blue-800 mb-2">Google Ads Tasks</h4>
                        <ul className="space-y-1">
                          {currentMonth.googleAds.map((task, index) => (
                            <li key={index} className="text-sm text-blue-700 flex items-center gap-2">
                              <CheckCircle className="w-3 h-3" />
                              {task}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg">
                        <h4 className="font-medium text-green-800 mb-2">Meta Ads Tasks</h4>
                        <ul className="space-y-1">
                          {currentMonth.metaAds.map((task, index) => (
                            <li key={index} className="text-sm text-green-700 flex items-center gap-2">
                              <CheckCircle className="w-3 h-3" />
                              {task}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="p-4 bg-yellow-50 rounded-lg">
                      <h4 className="font-medium text-yellow-800 mb-2">Key Performance Indicators</h4>
                      <div className="flex flex-wrap gap-2">
                        {currentMonth.kpis.map((kpi, index) => (
                          <span key={index} className="px-3 py-1 bg-yellow-200 text-yellow-800 rounded-full text-sm">
                            {kpi}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Channel-Specific Campaigns */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-6">Campaign Recommendations</h2>
              
              <div className="space-y-6">
                {/* Google Ads */}
                <div>
                  <button
                    onClick={() => toggleChannelExpansion('google')}
                    className="flex items-center justify-between w-full p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-600 rounded flex items-center justify-center">
                        <Search className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-blue-900">Google Ads Campaigns</h3>
                        <p className="text-sm text-blue-700">{roadmapData.campaignStrategy.googleAds.length} campaigns recommended</p>
                      </div>
                    </div>
                    {expandedChannels.has('google') ? 
                      <ChevronDown className="w-5 h-5 text-blue-600" /> : 
                      <ChevronRight className="w-5 h-5 text-blue-600" />
                    }
                  </button>
                  
                  {expandedChannels.has('google') && (
                    <div className="mt-4 space-y-4">
                      {roadmapData.campaignStrategy.googleAds.map((campaign, index) => (
                        <div key={index} className="p-4 border border-blue-200 rounded-lg">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCampaignTypeColor(campaign.type)}`}>
                                {campaign.type}
                              </span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(campaign.priority)}`}>
                                {campaign.priority} Priority
                              </span>
                            </div>
                            <span className="text-sm text-gray-600 flex items-center gap-1">
                              <DollarSign className="w-3 h-3" />
                              {campaign.budget}
                            </span>
                          </div>
                          
                          <h4 className="font-semibold text-gray-800 mb-2">{campaign.name}</h4>
                          <p className="text-sm text-gray-600 mb-3">{campaign.description}</p>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <h5 className="text-xs font-medium text-gray-500 uppercase mb-2">Target Categories</h5>
                              <div className="flex flex-wrap gap-1">
                                {campaign.targetCategories.slice(0, 3).map((category, idx) => (
                                  <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                                    {category}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <div>
                              <h5 className="text-xs font-medium text-gray-500 uppercase mb-2">Key Metrics</h5>
                              <div className="flex flex-wrap gap-1">
                                {campaign.kpis.slice(0, 3).map((kpi, idx) => (
                                  <span key={idx} className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                                    {kpi}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Meta Ads */}
                <div>
                  <button
                    onClick={() => toggleChannelExpansion('meta')}
                    className="flex items-center justify-between w-full p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-500 rounded flex items-center justify-center">
                        <Users className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-blue-900">Meta Ads Campaigns</h3>
                        <p className="text-sm text-blue-700">{roadmapData.campaignStrategy.metaAds.length} campaigns recommended</p>
                      </div>
                    </div>
                    {expandedChannels.has('meta') ? 
                      <ChevronDown className="w-5 h-5 text-blue-600" /> : 
                      <ChevronRight className="w-5 h-5 text-blue-600" />
                    }
                  </button>
                  
                  {expandedChannels.has('meta') && (
                    <div className="mt-4 space-y-4">
                      {roadmapData.campaignStrategy.metaAds.map((campaign, index) => (
                        <div key={index} className="p-4 border border-blue-200 rounded-lg">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCampaignTypeColor(campaign.type)}`}>
                                {campaign.type}
                              </span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(campaign.priority)}`}>
                                {campaign.priority} Priority
                              </span>
                            </div>
                            <span className="text-sm text-gray-600 flex items-center gap-1">
                              <DollarSign className="w-3 h-3" />
                              {campaign.budget}
                            </span>
                          </div>
                          
                          <h4 className="font-semibold text-gray-800 mb-2">{campaign.name}</h4>
                          <p className="text-sm text-gray-600 mb-3">{campaign.description}</p>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <h5 className="text-xs font-medium text-gray-500 uppercase mb-2">Target Categories</h5>
                              <div className="flex flex-wrap gap-1">
                                {campaign.targetCategories.slice(0, 3).map((category, idx) => (
                                  <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                                    {category}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <div>
                              <h5 className="text-xs font-medium text-gray-500 uppercase mb-2">Key Metrics</h5>
                              <div className="flex flex-wrap gap-1">
                                {campaign.kpis.slice(0, 3).map((kpi, idx) => (
                                  <span key={idx} className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                                    {kpi}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Projected Results */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-green-600" />
                Projected Results
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {Object.entries(roadmapData.projectedResults).map(([period, results]) => (
                  <div key={period} className="text-center p-4 border border-gray-200 rounded-lg">
                    <h3 className="font-medium text-gray-800 mb-3 capitalize">
                      {period.replace('month', 'Month ')}
                    </h3>
                    <div className="space-y-2">
                      <div className="text-sm">
                        <span className="text-gray-600">Traffic: </span>
                        <span className="font-medium text-green-600">{results.traffic}</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-600">Leads: </span>
                        <span className="font-medium text-green-600">{results.leads}</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-600">ROAS: </span>
                        <span className="font-medium text-blue-600">{results.roas}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Budget Range: </span>
                    <span className="text-gray-600">{roadmapData.totalBudgetRange}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Timeline: </span>
                    <span className="text-gray-600">{roadmapData.implementationTimeline}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Next Steps */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-yellow-500" />
                Ready to Start Your Momentum?
              </h2>
              <p className="text-gray-600 mb-4">
                Your custom marketing roadmap is ready! This comprehensive strategy is based on your actual website structure and AI-powered business analysis.
              </p>
              <div className="flex flex-wrap gap-3">
                <button className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                  <Play className="w-4 h-4" />
                  Start Implementation
                </button>
                <button className="flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                  <Calendar className="w-4 h-4" />
                  Schedule Consultation
                </button>
                <button className="flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                  <ExternalLink className="w-4 h-4" />
                  View Full Strategy
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 